import { createRouter, createWebHistory, type RouteMeta } from 'vue-router'
import { useAccountStore } from '@/stores'
import i18n from '@/i18n'
import DashboardLayoutVue from '@/layouts/dashboard.vue'

interface IRouteMeta {
  title: string
  requiresAuth?: boolean
}

// meta.title holds an i18n key (routeTitles.*) — translated at render time
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/home',
    },
    {
      path: '/home',
      component: DashboardLayoutVue,
      meta: {
        title: 'routeTitles.home',
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/views/dashboard/Home.vue'),
          meta: {
            title: 'routeTitles.home',
          } as RouteMeta & IRouteMeta,
        },
        {
          path: 'user',
          name: 'user_index',
          component: () => import('@/views/dashboard/user/Index.vue'),
          meta: {
            title: 'routeTitles.user',
          } as RouteMeta & IRouteMeta,
        },
        {
          path: 'settings',
          name: 'settings_index',
          component: () => import('@/views/dashboard/settings/Index.vue'),
          meta: {
            title: 'routeTitles.settings',
          } as RouteMeta & IRouteMeta,
        },
        {
          path: 'devices',
          name: 'devices_index',
          component: () => import('@/views/dashboard/devices/Index.vue'),
          meta: {
            title: 'routeTitles.devices',
          } as RouteMeta & IRouteMeta,
        },
      ],
    },
    {
      path: '/billing',
      redirect: { path: '/home', hash: '#billing-section' },
    },
    {
      path: '/billing/success',
      name: 'billing-success',
      component: () => import('@/views/billing/success.vue'),
      meta: {
        title: 'routeTitles.billingSuccess',
        requiresAuth: true,
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/login/index.vue'),
      meta: {
        title: 'routeTitles.login',
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/register/index.vue'),
      meta: {
        title: 'routeTitles.register',
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/forgot-password/index.vue'),
      meta: {
        title: 'routeTitles.forgotPassword',
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/reset-password/index.vue'),
      meta: {
        title: 'routeTitles.resetPassword',
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/legal/:type',
      name: 'legal',
      component: () => import('@/views/legal/index.vue'),
      meta: {
        title: 'routeTitles.legal',
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/subscribe',
      name: 'subscribe',
      component: () => import('@/views/subscribe/index.vue'),
      meta: {
        title: 'routeTitles.subscribe',
        requiresAuth: true,
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/:pathMatch(.*)',
      name: 'not-found',
      component: () => import('@/views/404.vue'),
      meta: {
        title: 'routeTitles.notFound',
      } as RouteMeta & IRouteMeta,
    },
  ],
})

router.beforeEach((to) => {
  document.title = i18n.global.t(String(to.meta.title))

  const accountStore = useAccountStore()
  const requiresAuth = to.meta.requiresAuth

  if (requiresAuth && !accountStore.account.accessToken) {
    accountStore.setRedirectAfterLogin(to.fullPath)
    return { name: 'login' }
  }
})

export default router
